import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

const columns = [
  { key: "name", label: "Nombre", width: "minmax(190px, 1.7fr)" },
  { key: "sector", label: "Sector", width: "minmax(110px, 1fr)" },
  { key: "price", label: "Precio", width: "minmax(115px, 1fr)" },
  { key: "cost", label: "Costo", width: "minmax(115px, 1fr)" },
  { key: "content", label: "Contenido", width: "minmax(110px, .9fr)" },
  { key: "active", label: "Activo", width: "minmax(100px, .85fr)" },
  { key: "actions", label: "Acciones", width: "minmax(140px, 1fr)" },
];

const gridSx = {
  display: "grid",
  gridTemplateColumns: columns.map((column) => column.width).join(" "),
  columnGap: 0.75,
  alignItems: "center",
  "& > *": { minWidth: 0 },
};

function TextCell({ children, title }) {
  return (
    <Typography
      noWrap
      title={title || (typeof children === "string" ? children : undefined)}
      sx={{
        color: "primary.900",
        fontSize: 14,
        fontWeight: 600,
        textAlign: "center",
      }}
    >
      {children}
    </Typography>
  );
}

function StatusPill({
  active,
  activeLabel = "Activo",
  inactiveLabel = "Inactivo",
  width = 78,
}) {
  return (
    <Box
      sx={{
        width,
        px: 0.8,
        py: 0.55,
        borderRadius: "20px",
        bgcolor: active ? "rgba(33, 150, 83, 0.16)" : "rgba(195, 60, 60, 0.16)",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.28)",
      }}
    >
      <Typography
        sx={{
          color: active ? "rgba(18, 111, 63, 1)" : "rgba(160, 49, 49, 1)",
          fontSize: 12,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        {active ? activeLabel : inactiveLabel}
      </Typography>
    </Box>
  );
}

export default function SaleProductsTable({
  rows = [],
  loading = false,
  onEdit,
  onView,
  onReview,
  onToggleActive,
  onDelete,
  updatingProductId,
  deletingProductId,
  emptyMessage = "Todavía no hay productos de venta cargados.",
}) {
  return (
    <Box sx={{ flex: 1, minHeight: 0, borderRadius: "20px", overflow: "hidden" }}>
      <Box sx={{ height: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <Box
          sx={{
            minWidth: 1020,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              ...gridSx,
              bgcolor: "neutral.600",
              flexShrink: 0,
              px: 2,
              py: 1.6,
              overflowY: "hidden",
              scrollbarGutter: "stable",
            }}
          >
            {columns.map((column) => (
              <Typography
                key={column.key}
                sx={{
                  color: "logoblanco",
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  textAlign: "center",
                }}
              >
                {column.label}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              bgcolor: "neutral.50",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              scrollbarGutter: "stable",
            }}
          >
            {rows.map((product) => (
              <Box
                key={product.id}
                sx={{
                  ...gridSx,
                  minHeight: 58,
                  px: 2,
                  py: 1.2,
                  borderBottom: "1px solid rgba(0,0,0,.06)",
                  "&:hover": { bgcolor: "rgba(0,0,0,.03)" },
                }}
              >
                <TextCell>{product.name}</TextCell>
                <TextCell>{product.sectorLabel}</TextCell>
                <TextCell>{product.priceLabel}</TextCell>
                <TextCell>{product.costLabel}</TextCell>

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Tooltip title={product.contentDetail} arrow>
                    <Button
                      type="button"
                      aria-label={`Ver más contenido de ${product.name}`}
                      size="small"
                      disabled={loading}
                      onClick={() => onView?.(product)}
                      sx={{
                        width: 88,
                        minWidth: 88,
                        py: 0.55,
                        px: 0.8,
                        borderRadius: "20px",
                        bgcolor: "secondary.500",
                        color: "logoblanco",
                        textTransform: "none",
                        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.35)",
                        "&:hover": { bgcolor: "secondary.600" },
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                        Ver más
                      </Typography>
                    </Button>
                  </Tooltip>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <StatusPill active={product.isActive} />
                </Box>

                <Stack direction="row" spacing={0.45} sx={{ justifyContent: "center" }}>
                  <Tooltip title="Editar producto" arrow>
                    <IconButton
                      type="button"
                      size="small"
                      aria-label={`Editar ${product.name}`}
                      disabled={loading}
                      onClick={() => onEdit?.(product)}
                      sx={{
                        width: 29,
                        height: 29,
                        bgcolor: "neutral.100",
                      }}
                    >
                    <EditIcon sx={{ fontSize: 17, color: "primary.900" }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip
                    title={
                      product.isActive
                        ? "Desactivar producto"
                        : "Activar producto"
                    }
                    arrow
                  >
                    <span>
                      <IconButton
                        type="button"
                        size="small"
                        aria-label={`${product.isActive ? "Desactivar" : "Activar"} ${product.name}`}
                        disabled={
                          loading ||
                          updatingProductId === product.id
                        }
                        onClick={() => onToggleActive?.(product)}
                        sx={{
                          width: 29,
                          height: 29,
                          bgcolor: "neutral.100",
                          "&:hover": { bgcolor: "secondary.100" },
                        }}
                      >
                        {updatingProductId === product.id ? (
                          <CircularProgress size={15} />
                        ) : (
                          <PowerSettingsNewIcon sx={{ fontSize: 17, color: "primary.900" }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Eliminar producto" arrow>
                    <span>
                      <IconButton
                        type="button"
                        size="small"
                        aria-label={`Eliminar ${product.name}`}
                        disabled={
                          loading ||
                          updatingProductId === product.id ||
                          deletingProductId === product.id
                        }
                        onClick={() => onDelete?.(product)}
                        sx={{
                          width: 29,
                          height: 29,
                          bgcolor: "neutral.100",
                          "&:hover": { bgcolor: "secondary.100" },
                        }}
                      >
                        {deletingProductId === product.id ? (
                          <CircularProgress size={15} />
                        ) : (
                          <DeleteOutlineIcon sx={{ fontSize: 17, color: "primary.900" }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>

                  {product.costStatus === "REQUIRES_REVIEW" ? (
                    <Tooltip
                      title={`No disponible · ${
                        product.costReviewReasonsLabel || "El costo requiere revisión."
                      }`}
                      arrow
                    >
                      <IconButton
                        type="button"
                        size="small"
                        aria-label={`Revisar ${product.name}`}
                        disabled={loading || updatingProductId === product.id}
                        onClick={() => onReview?.(product)}
                        sx={{
                          width: 29,
                          height: 29,
                          bgcolor: "rgba(195, 60, 60, 0.12)",
                          "&:hover": { bgcolor: "rgba(195, 60, 60, 0.2)" },
                        }}
                      >
                        <WarningAmberIcon sx={{ fontSize: 17, color: "error.main" }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Stack>
              </Box>
            ))}

            {rows.length === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography sx={{ color: "neutral.700", fontWeight: 600 }}>
                  {loading ? "Cargando productos de venta..." : emptyMessage}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
